import { parseMagicMd, type ParsedDocument } from "@magic-md/core";
import { isMarkdownFile } from "./file-type";
import type {
    MarkdownParseWorkerRequest,
    MarkdownParseWorkerResponse,
} from "./markdown-parse-worker";

/** Below this size, synchronous parsing is cheaper than a Worker round trip. */
export const OFF_THREAD_PARSE_MIN_CHARS = 64 * 1024;

interface PendingParse {
    resolve: (parsed: ParsedDocument) => void;
    reject: (error: Error) => void;
}

let sharedWorker: Worker | null = null;
let workerUnavailable = false;
let nextRequestId = 1;
const pendingParses = new Map<number, PendingParse>();

function rejectPendingParses(error: Error): void {
    for (const pending of pendingParses.values()) pending.reject(error);
    pendingParses.clear();
}

function disableWorker(error: Error): void {
    workerUnavailable = true;
    sharedWorker?.terminate();
    sharedWorker = null;
    rejectPendingParses(error);
}

function getSharedWorker(): Worker {
    if (sharedWorker) return sharedWorker;
    if (workerUnavailable || typeof Worker === "undefined") {
        throw new Error("Markdown parse Worker is unavailable");
    }

    const worker = new Worker(
        new URL("./markdown-parse-worker.ts", import.meta.url),
        { type: "module", name: "magic-md-markdown-parser" },
    );
    worker.onmessage = (event: MessageEvent<MarkdownParseWorkerResponse>) => {
        const response = event.data;
        const pending = pendingParses.get(response.requestId);
        if (!pending) return;
        pendingParses.delete(response.requestId);
        if (response.type === "result") {
            pending.resolve(response.parsed);
        } else {
            pending.reject(new Error(response.message));
        }
    };
    worker.onerror = (event) => {
        disableWorker(new Error(event.message || "Markdown parse Worker failed"));
    };
    sharedWorker = worker;
    return worker;
}

function parseInWorker(content: string): Promise<ParsedDocument> {
    const worker = getSharedWorker();
    const requestId = nextRequestId++;
    return new Promise((resolve, reject) => {
        pendingParses.set(requestId, { resolve, reject });
        const request: MarkdownParseWorkerRequest = {
            type: "parse",
            requestId,
            content,
        };
        try {
            worker.postMessage(request);
        } catch (error) {
            pendingParses.delete(requestId);
            reject(error instanceof Error ? error : new Error(String(error)));
        }
    });
}

export function parseMagicMdDocumentSync(
    content: string,
    filePath?: string,
): ParsedDocument | null {
    if (!isMarkdownFile(filePath)) return null;
    return parseMagicMd(content);
}

export function shouldParseMagicMdOffThread(
    content: string,
    filePath?: string,
): boolean {
    return isMarkdownFile(filePath) &&
        content.length >= OFF_THREAD_PARSE_MIN_CHARS &&
        !workerUnavailable &&
        typeof Worker !== "undefined";
}

export async function parseMagicMdDocumentAsync(
    content: string,
    filePath?: string,
): Promise<ParsedDocument | null> {
    if (!shouldParseMagicMdOffThread(content, filePath)) {
        return parseMagicMdDocumentSync(content, filePath);
    }
    try {
        return await parseInWorker(content);
    } catch {
        // A worker can be unavailable under SSR, CSP, or an older webview.
        // Preserve editing correctness by falling back to the established path.
        return parseMagicMdDocumentSync(content, filePath);
    }
}

/** Test-only lifecycle reset; not exported from the package public entrypoint. */
export function resetMarkdownParseWorkerForTests(): void {
    sharedWorker?.terminate();
    sharedWorker = null;
    workerUnavailable = false;
    nextRequestId = 1;
    rejectPendingParses(new Error("Markdown parse Worker reset"));
}
